import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CaretLeft, CaretRight, Coins } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PORTAL_COLUMNS_MAIN_PRODUCTS, PORTAL_COLUMNS_MOBILE, PORTAL_COLUMNS_BEST_SELLING, livePhotoUrl, canSeeGT, stripGtColumns } from "@/lib/fmUtils";
import { useAuth } from "@/contexts/AuthContext";
import { useFmWritesEnabled } from "@/hooks/useFmWritesEnabled";
import { DrawerImagesSection } from "@/components/product-drawer/DrawerImagesSection";
import { DrawerDetailsSection, AutoGrowTextarea } from "@/components/product-drawer/DrawerDetailsSection";
import { DrawerVariantsTable } from "@/components/product-drawer/DrawerVariantsTable";
import { DrawerFooterActions } from "@/components/product-drawer/DrawerFooterActions";
import { ChinaQuoteDialog } from "@/components/ChinaQuoteLink";

export default function ProductDrawer({
  open,
  onOpenChange,
  layout,
  record,
  fields,
  onSaved,
  onCartUpdated,
  onNavigate,
  canGoPrev,
  canGoNext,
  photoVersion,
  onPhotoUploaded,
  readOnly,
  bestSelling = false,
  allowAddToCart = false,
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const fmWritesEnabled = useFmWritesEnabled();
  // Edit / photo-upload capability is gated by BOTH the user's role AND the
  // global FileMaker read-only flag. The Web App must never surface UI that
  // would attempt a write when the backend has writes disabled - the backend
  // guard returns 403 either way, but hiding the button avoids user confusion.
  const canEdit = !readOnly && fmWritesEnabled && user && (user.role === "admin" || user.role === "office");
  const canUploadPhoto = !readOnly && fmWritesEnabled && user?.role === "admin";
  const showGt = canSeeGT(user);
  const isCreate = !record;
  const [editMode, setEditMode] = useState(isCreate);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const canSeeCostDetail = user?.role === "admin" || user?.role === "office";
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [zoomingField, setZoomingField] = useState(null);
  const [imgErrorMap, setImgErrorMap] = useState({});
  const [localVersions, setLocalVersions] = useState({});
  const fileInputRef = useRef(null);
  const uploadFieldRef = useRef(null);

  useEffect(() => {
    setEditMode(isCreate);
    setFormData(record?.fieldData ? { ...record.fieldData } : {});
    setImgErrorMap({});
    setLocalVersions({});
  }, [record, isCreate, open]);

  const HIDDEN_FIELDS = [
    "photo_modified",
    "best_selling",
    "selling_history_date",
    "date1",
    "inventory type",
    ...(showGt ? [] : ["product::GT_SKU"]),
    ...(bestSelling ? [] : ["selling_history"]),
  ];
  const HEADER_FIELD_NAMES = ["item no", "description", "category", "sub_category", "specification"];
  const displayFields = fields.filter((f) => !HIDDEN_FIELDS.includes(f.name));
  const containerFields = displayFields.filter((f) => f.result === "container");
  const headerFields = readOnly
    ? []
    : HEADER_FIELD_NAMES.map((name) => displayFields.find((f) => f.name === name)).filter(Boolean);
  const editableFields = displayFields.filter(
    (f) => f.type === "normal" && f.result !== "container" && (readOnly || !HEADER_FIELD_NAMES.includes(f.name))
  );
  const readonlyFields = displayFields.filter((f) => f.type !== "normal" && f.result !== "container");
  const portalRows = record?.portalData?.product || [];
  const basePortalColumns = readOnly ? PORTAL_COLUMNS_MOBILE : bestSelling ? PORTAL_COLUMNS_BEST_SELLING : PORTAL_COLUMNS_MAIN_PRODUCTS;
  const portalColumns = showGt ? basePortalColumns : stripGtColumns(basePortalColumns);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const triggerPhotoUpload = (fieldName) => {
    uploadFieldRef.current = fieldName;
    fileInputRef.current?.click();
  };

  const handlePhotoFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const fieldName = uploadFieldRef.current;
    e.target.value = "";
    if (!file || !fieldName || !record?.recordId) return;
    setUploadingField(fieldName);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(
        `/fm/records/${record.recordId}/photo?layout=${encodeURIComponent(layout)}&field=${encodeURIComponent(fieldName)}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImgErrorMap((prev) => ({ ...prev, [fieldName]: false }));
      setLocalVersions((prev) => ({ ...prev, [fieldName]: Date.now() }));
      toast({ title: "Image updated" });
      onPhotoUploaded?.();
    } catch (err) {
      toast({
        title: "Image upload failed",
        description: formatApiErrorDetail(err.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setUploadingField(null);
    }
  };

  const handlePhotoZoom = async (fieldName, direction) => {
    if (!record?.recordId) return;
    setZoomingField(fieldName);
    try {
      await api.post(`/fm/products/${record.recordId}/photo-zoom`, { direction });
      setLocalVersions((prev) => ({ ...prev, [fieldName]: Date.now() }));
      onPhotoUploaded?.();
    } catch (err) {
      toast({
        title: "Failed to adjust photo",
        description: formatApiErrorDetail(err.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setZoomingField(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      [...headerFields, ...editableFields].forEach((f) => {
        if (formData[f.name] !== undefined) payload[f.name] = formData[f.name];
      });
      if (isCreate) {
        await api.post(`/fm/records?layout=${encodeURIComponent(layout)}`, payload);
        toast({ title: "Product created" });
      } else {
        await api.patch(`/fm/records/${record.recordId}?layout=${encodeURIComponent(layout)}`, payload);
        toast({ title: "Product updated" });
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Save failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddToCart = async () => {
    setAddingToCart(true);
    try {
      await api.post("/cart/items", { main_product_record_id: record.recordId });
      toast({ title: "Added to cart" });
      onCartUpdated?.();
    } catch (e) {
      toast({
        title: "Could not add to cart",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setAddingToCart(false);
    }
  };

  const canBlock = user && (user.role === "admin" || user.role === "office") && !isCreate && !!record?.recordId;
  const canQtyCoeffEdit = user && (user.role === "admin" || user.role === "office");
  const [blocking, setBlocking] = useState(false);
  const [qtyCoeffInput, setQtyCoeffInput] = useState("0.85");
  const [savingQtyCoeff, setSavingQtyCoeff] = useState(false);

  useEffect(() => {
    setQtyCoeffInput(String(record?.qty_coefficient ?? 0.85));
  }, [record]);

  const handleSaveQtyCoeff = async () => {
    const val = parseFloat(qtyCoeffInput);
    if (!val || val <= 0) {
      toast({ title: "Invalid coefficient", variant: "destructive" });
      return;
    }
    setSavingQtyCoeff(true);
    try {
      await api.put(`/products/${record.recordId}/qty-coefficient`, { coefficient: val });
      toast({ title: "Qty coefficient saved", description: "Quote quantities will use the new coefficient." });
      onSaved?.();
    } catch (e) {
      toast({
        title: "Failed to save qty coefficient",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSavingQtyCoeff(false);
    }
  };
  const handleToggleBlock = async () => {
    setBlocking(true);
    try {
      if (record.block) {
        await api.delete(`/products/${record.recordId}/block`);
        toast({ title: "Product unblocked" });
      } else {
        await api.put(`/products/${record.recordId}/block`);
        toast({ title: "Product blocked", description: "It will no longer appear in quote exports/prints." });
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Block action failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setBlocking(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto rounded-none border-l border-zinc-300 sm:max-w-2xl"
        data-testid="product-drawer"
      >
        <SheetHeader>
          <div className="flex items-center justify-between gap-2 pr-8">
            <SheetTitle className="font-heading tracking-tight" data-testid="product-drawer-title">
              {isCreate ? "New Product" : record?.fieldData?.["item no_main"] || record?.fieldData?.["item no"] || `Record ${record?.recordId}`}
            </SheetTitle>
            {!isCreate && onNavigate && (
              <div className="flex items-center gap-1">
                {bestSelling && canSeeCostDetail && record?.fieldData?.["item no"] && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm border-blue-400 bg-blue-50 px-2 h-auto py-1 text-xs text-blue-700 hover:bg-blue-100"
                    onClick={() => setCostDialogOpen(true)}
                    data-testid="product-drawer-cost-detail-button"
                  >
                    <Coins size={14} className="mr-1" />
                    Cost detail
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm border-zinc-300 p-1.5 h-auto"
                  disabled={!canGoPrev}
                  onClick={() => onNavigate("prev")}
                  data-testid="product-drawer-prev-button"
                >
                  <CaretLeft size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm border-zinc-300 p-1.5 h-auto"
                  disabled={!canGoNext}
                  onClick={() => onNavigate("next")}
                  data-testid="product-drawer-next-button"
                >
                  <CaretRight size={14} />
                </Button>
              </div>
            )}
          </div>
          <SheetDescription className="text-xs uppercase tracking-[0.05em]">
            Layout: {layout}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 flex flex-col gap-6">
          {record?.block && (
            <div
              className="flex items-center gap-2 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="product-blocked-banner"
            >
              <span className="rounded-sm bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                Blocked
              </span>
              <span>
                Blocked by <strong>{record.block.blocked_by || "-"}</strong>
                {record.block.blocked_at ? ` on ${new Date(record.block.blocked_at).toLocaleDateString()}` : ""} — excluded
                from quote exports and prints.
              </span>
            </div>
          )}
          {readOnly ? (
            <DrawerImagesSection
              containerFields={containerFields}
              layout={layout}
              record={record}
              photoVersion={photoVersion}
              localVersions={localVersions}
              imgErrorMap={imgErrorMap}
              onImgError={(fieldName) => setImgErrorMap((prev) => ({ ...prev, [fieldName]: true }))}
              canEdit={canEdit}
              canUpload={canUploadPhoto}
              isCreate={isCreate}
              uploadingField={uploadingField}
              zoomingField={zoomingField}
              onTriggerUpload={triggerPhotoUpload}
              onPhotoZoom={handlePhotoZoom}
              fileInputRef={fileInputRef}
              onFileSelected={handlePhotoFileSelected}
            />
          ) : (
            <>
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="w-full shrink-0 sm:w-[45%]">
                  <DrawerImagesSection
                    containerFields={bestSelling ? containerFields.filter((f) => f.name === "photo") : containerFields}
                    layout={layout}
                    record={record}
                    photoVersion={photoVersion}
                    localVersions={localVersions}
                    imgErrorMap={imgErrorMap}
                    onImgError={(fieldName) => setImgErrorMap((prev) => ({ ...prev, [fieldName]: true }))}
                    canEdit={canEdit}
                    canUpload={canUploadPhoto}
                    isCreate={isCreate}
                    uploadingField={uploadingField}
                    zoomingField={zoomingField}
                    onTriggerUpload={triggerPhotoUpload}
                    onPhotoZoom={handlePhotoZoom}
                    fileInputRef={fileInputRef}
                    onFileSelected={handlePhotoFileSelected}
                    showZoomControl={!bestSelling}
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-3" data-testid="product-drawer-header-fields">
                  {(() => {
                    const renderHeaderField = (f) =>
                      f && (
                        <div key={f.name} className="flex flex-col gap-1">
                          <Label className="text-xs text-zinc-500">
                            {{ "item no": "Item Number", description: "Description", category: "Category", sub_category: "Sub Category", specification: "Specification" }[f.name] || f.name}
                          </Label>
                          {editMode ? (
                            f.name === "specification" ? (
                              <AutoGrowTextarea
                                value={formData[f.name] ?? ""}
                                onChange={(e) => handleChange(f.name, e.target.value)}
                                data-testid={`product-field-input-${f.name}`}
                              />
                            ) : (
                              <Input
                                value={formData[f.name] ?? ""}
                                onChange={(e) => handleChange(f.name, e.target.value)}
                                className="rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                                data-testid={`product-field-input-${f.name}`}
                              />
                            )
                          ) : (
                            <div
                              className={`rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-sm text-zinc-800 ${
                                f.name === "item no" ? "font-semibold text-zinc-900" : ""
                              } ${f.name === "specification" ? "whitespace-pre-wrap" : ""}`}
                              data-testid={`product-field-value-${f.name}`}
                            >
                              {f.name === "specification"
                                ? String(formData[f.name] ?? "").replace(/\r/g, "\n").trim() || "-"
                                : String(formData[f.name] ?? "").replace(/\r/g, " ").trim() || "-"}
                            </div>
                          )}
                        </div>
                      );
                    if (!bestSelling) return headerFields.map(renderHeaderField);
                    const byName = (n) => headerFields.find((f) => f.name === n);
                    return (
                      <>
                        {renderHeaderField(byName("item no"))}
                        {renderHeaderField(byName("description"))}
                        <div className="grid grid-cols-2 gap-3" data-testid="category-subcategory-row">
                          {renderHeaderField(byName("category"))}
                          {renderHeaderField(byName("sub_category"))}
                        </div>
                        {renderHeaderField(byName("specification"))}
                      </>
                    );
                  })()}
                </div>
              </div>
              {bestSelling && record?.recordId && formData["selling_history"] && (
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start" data-testid="best-selling-history-image-block">
                  <div className="w-full shrink-0 sm:w-[58.5%]">
                    <img
                      src={livePhotoUrl(layout, record.recordId, "selling_history", photoVersion)}
                      alt="selling history"
                      className="w-full rounded-sm border border-zinc-300 object-contain"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs text-zinc-500">Distribution Date</Label>
                    <div
                      className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-sm text-zinc-800"
                      data-testid="product-field-value-distribution-date"
                    >
                      {String(formData["selling_history_date"] ?? "").trim() || "-"}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {!bestSelling && (
            <DrawerDetailsSection
              readOnly={readOnly}
              formData={formData}
              editMode={editMode}
              editableFields={editableFields}
              readonlyFields={readonlyFields}
              onChange={handleChange}
            />
          )}

          <DrawerVariantsTable
            portalRows={portalRows}
            portalColumns={portalColumns}
            layout={layout}
            recordId={record?.recordId}
            canEditVariantPrice={canEdit && !isCreate && !!record?.recordId}
            onVariantSaved={onSaved}
          />

          {!isCreate && record?.recordId && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2"
              data-testid="qty-coefficient-section"
            >
              <span className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Qty Coefficient</span>
              {canQtyCoeffEdit ? (
                <>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={qtyCoeffInput}
                    onChange={(e) => setQtyCoeffInput(e.target.value)}
                    className="h-8 w-24 rounded-sm border-zinc-300 text-right text-sm font-mono-data"
                    data-testid="qty-coefficient-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-sm border-zinc-300"
                    disabled={savingQtyCoeff || String(record?.qty_coefficient ?? 0.85) === qtyCoeffInput}
                    onClick={handleSaveQtyCoeff}
                    data-testid="qty-coefficient-save-button"
                  >
                    {savingQtyCoeff ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <span className="text-sm font-mono-data text-zinc-800" data-testid="qty-coefficient-value">
                  {record?.qty_coefficient ?? 0.85}
                </span>
              )}
              <span className="text-[11px] text-zinc-500">Quote QTY = QTY × coefficient (default 0.85)</span>
            </div>
          )}
        </div>

        <DrawerFooterActions
          readOnly={readOnly}
          allowAddToCart={allowAddToCart}
          isCreate={isCreate}
          editMode={editMode}
          canEdit={canEdit}
          addingToCart={addingToCart}
          saving={saving}
          onAddToCart={handleAddToCart}
          onEdit={() => setEditMode(true)}
          onCancel={() => (isCreate ? onOpenChange(false) : setEditMode(false))}
          onSave={handleSave}
          canBlock={canBlock}
          isBlocked={!!record?.block}
          blocking={blocking}
          onToggleBlock={handleToggleBlock}
        />

        {bestSelling && canSeeCostDetail && record?.fieldData?.["item no"] && (
          <ChinaQuoteDialog
            itemNo={record.fieldData["item no"]}
            open={costDialogOpen}
            onOpenChange={setCostDialogOpen}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
